import {
  Datagrid,
  FunctionField,
  Pagination,
  ReferenceManyField,
  TextField,
} from 'react-admin';
import CreateIcon from '@mui/icons-material/Create';
import GetAppIcon from '@mui/icons-material/GetApp';
import { IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { analyticsTrack } from '@pumpkincare/analytics';
import { formatClaimAttachmentType } from '@pumpkincare/claims';

import { MGA_DOCUMENT_TYPES } from '../../../constants/claimAttachmentDocumentTypes';
import { getDateAsFormattedUTC } from '../../../shared/utils';
import { useMedicalRecordsRequestModal } from '../../medical-records';
import DeleteClaimAttachmentButton from '../ClaimShow/view/DeleteClaimAttachmentButton';
import { claimAttachmentTableStyle } from './claim-attachment-table-style';

function ClaimAttachmentTable(props) {
  const classes = claimAttachmentTableStyle();
  const { openViewDetails } = useMedicalRecordsRequestModal();

  const {
    onEditClick = () => {},
    showDownloadButton = true,
    showEditButton = false,
    showDeleteButton = false,
    showFileType = true,
    currentOpsUserData,
    disableButton = false,
    target = 'claim_id',
    changeReference,
    isPetView = false,
    ...rest
  } = props;

  const reference = changeReference || `claims/${props.claimId}/documents`;

  function handleDownloadAttachment(record) {
    if (
      record.type &&
      MGA_DOCUMENT_TYPES[record.type] === MGA_DOCUMENT_TYPES.claim_invoice
    ) {
      analyticsTrack({
        event: 'clicked_download_invoice_claims',
        label: 'claims invoice download',
        category: 'claims admin actions',
        claim_id: record?.claim_id,
        ops_user_id: currentOpsUserData?.id,
        file_name: record?.source_file_name,
      });
    }

    window.open(record.document_signed_url);
  }

  return (
    <ReferenceManyField
      {...rest}
      reference={reference}
      target={target}
      label=''
      sort={{ field: 'created_at', order: 'DESC' }}
      pagination={
        <Pagination
          sx={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      }
    >
      <Datagrid
        bulkActionButtons={false}
        classes={{
          headerCell: classes.headerCell,
          table: classes.table,
          rowCell: classes.rowCell,
        }}
        empty={<Typography variant='body2'>No files found</Typography>}
        rowClick={false}
      >
        <FunctionField
          label='DATE'
          source='created_at'
          render={(record = {}) =>
            isPetView && record?.medical_records_request_id ? (
              <div>
                <span className={classes.dateText}>
                  {getDateAsFormattedUTC(record.created_at, 'MM/DD/YYYY')}
                </span>
                <button
                  onClick={() => openViewDetails(record.medical_records_request_id)}
                  className={classes.link}
                >
                  MRC ID{record?.medical_records_request_id}
                </button>
              </div>
            ) : (
              getDateAsFormattedUTC(record.created_at, 'MM/DD/YYYY')
            )
          }
          className={classes.fontStyle}
        />

        <TextField
          label='FILE NAME'
          source='source_file_name'
          className={classes.fontStyle}
        />

        {showFileType ? (
          <FunctionField
            label='FILE TYPE'
            source='type'
            render={(record = {}) => formatClaimAttachmentType(record.type)}
            className={classes.fontStyle}
          />
        ) : null}

        <FunctionField
          label=''
          render={(record = {}) =>
            showDownloadButton ? (
              <IconButton
                style={{ padding: '0px' }}
                onClick={() => handleDownloadAttachment(record)}
                size='large'
              >
                <GetAppIcon color='primary' />
              </IconButton>
            ) : null
          }
          className={classes.fontStyle}
        />

        {showEditButton ? (
          <FunctionField
            label=''
            render={(record = {}) => (
              <IconButton
                sx={{
                  padding: '0px',
                  color: '#3EC28F',
                }}
                onClick={() => onEditClick(record)}
                size='large'
                disabled={disableButton}
                data-testid='edit-attachment-button-claim-attachment-table'
              >
                <CreateIcon />
              </IconButton>
            )}
            className={classes.fontStyle}
          />
        ) : null}

        {showDeleteButton ? (
          <DeleteClaimAttachmentButton
            currentOpsUserData={currentOpsUserData}
            disableButton={disableButton}
            isPetView={isPetView}
          />
        ) : null}
      </Datagrid>
    </ReferenceManyField>
  );
}

ClaimAttachmentTable.propTypes = {
  onEditClick: PropTypes.func,
  showDownloadButton: PropTypes.bool,
  showEditButton: PropTypes.bool,
  showDeleteButton: PropTypes.bool,
  showFileType: PropTypes.bool,
  currentOpsUserData: PropTypes.shape({
    id: PropTypes.string,
  }),
  claimId: PropTypes.string,
  disableButton: PropTypes.bool,
  target: PropTypes.string,
  changeReference: PropTypes.string,
  isPetView: PropTypes.bool,
};

export default ClaimAttachmentTable;
